import React, { useEffect, useState } from "react";
import { UseAppDispatch } from "redux/store";
import { useSelector } from "react-redux";

import {
  setSameAsContactPerson,
  travelerInformationSelector,
  ITraveler,
  setTravelerState,
  ITravelerFormFields,
} from "redux/travelerInformation";
import { contactInformationSelector } from "redux/contactInformation";
import { itinerarySelector } from "redux/selectedItinerary";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import {
  addPaymentLine,
  removePaymentLine,
  IPaymentLine,
} from "redux/shoppingCart";
import { useTranslations } from "hooks/useTranslations";
import {
  Box,
  Checkbox,
  Collapse,
  FormControlLabel,
  FormLabel,
  SxProps,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { NameLengthNotification } from "../NameLengthNotification/NameLengthNotification";
import {
  dateTimeStringToDateObj,
  useConfig,
  TextField,
  DatePicker,
  RadioGroup,
} from "@thekilroygroup/shared-ui";
import { IAppConfigObject } from "appConfig.interfaces";
import { useForm } from "react-hook-form";
import { addDays, subYears, isValid as isValidFns, format } from "date-fns";

// Absolute lower bound for any date of birth, whatever the passenger type.
const MAX_PASSENGER_AGE = 120;

interface IProps {
  traveler: ITraveler;
  index: number;
  youthStudentCardPrice?: number | undefined;
  onValidityChange: (isValid: boolean) => void;
  shouldTriggerValidation?: boolean;
}

export default function TravelerInformation(props: IProps) {
  const {
    index,
    traveler,
    youthStudentCardPrice,
    onValidityChange,
    shouldTriggerValidation,
  } = props;

  const theme = useTheme();
  const { translations } = useTranslations();
  const {
    appConfig: { PRIMARY_LANGUAGE },
  } = useConfig<IAppConfigObject>();
  const content = translations.travelerInformation;
  const itinerary = useSelector(itinerarySelector);

  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [nameHasFocus, setNameHasFocus] = useState(false);
  const showNameLengthNotification =
    nameHasFocus && (PRIMARY_LANGUAGE === "nl" || PRIMARY_LANGUAGE === "sv");

  const contactInformationContent = translations.contactInformation;
  const formLabels = translations.formLabels;

  const dispatch = UseAppDispatch();
  const { sameAsContactPerson } = useSelector(travelerInformationSelector);
  const contactInformation = useSelector(contactInformationSelector);

  const {
    control,
    formState: { errors, isValid },
    setValue,
    resetField,
    trigger,
    watch,
  } = useForm({
    mode: "onTouched",
  });

  const handleCheckboxClick = (checked: boolean): void => {
    if (index !== 0) {
      return;
    }
    dispatch(setSameAsContactPerson(checked));
    if (checked) {
      resetField("firstName");
      setValue("firstName", contactInformation.firstName);
      resetField("lastName");
      setValue("lastName", contactInformation.lastName);
      resetField(`genderType--${index}`);
      setValue(`genderType--${index}`, contactInformation.genderType);
      setValue("middleName", contactInformation.middleName);
      trigger("dateOfBirth", {
        shouldFocus: true,
      });
    } else {
      resetField("firstName");
      resetField("middleName");
      resetField("lastName");
      resetField("genderType");
    }
  };

  const lastTravelDateTime =
    dateTimeStringToDateObj(
      itinerary.flights.slice(-1)[0].flightSegments.slice(-1)[0]
        .departureDateTime,
    ) || new Date();

  const ageRestrictions = {
    passengerMaxAge: addDays(
      subYears(lastTravelDateTime, MAX_PASSENGER_AGE),
      1,
    ),
    youthStudentMaxAge: addDays(
      subYears(
        lastTravelDateTime,
        itinerary?.youthStudentMaxAge || MAX_PASSENGER_AGE,
      ),
      1,
    ),
    childMaxAge: addDays(subYears(lastTravelDateTime, 12), 1),
    infantMaxAge: addDays(subYears(lastTravelDateTime, 2), 1),
  };

  const getMinDate = () => {
    if (traveler.passengerType === "STU" || traveler.passengerType === "YTH") {
      return ageRestrictions.youthStudentMaxAge;
    } else if (
      traveler.passengerType === "CNN" ||
      traveler.passengerType === "CHD"
    ) {
      return ageRestrictions.childMaxAge;
    } else if (traveler.passengerType === "INF") {
      return ageRestrictions.infantMaxAge;
    }
    return ageRestrictions.passengerMaxAge;
  };

  const getMaxDate = () => {
    if (
      traveler.passengerType === "ADT" ||
      traveler.passengerType === "STU" ||
      traveler.passengerType === "YTH"
    ) {
      return ageRestrictions.childMaxAge;
    } else if (
      traveler.passengerType === "CNN" ||
      traveler.passengerType === "CHD"
    ) {
      return ageRestrictions.infantMaxAge;
    }
    return new Date();
  };

  const validateAgeRestrictions = (value: string) => {
    const date = new Date(value);

    if (traveler.passengerType === "ADT") {
      if (date > ageRestrictions.childMaxAge) {
        return translations.formValidations.minAge.replace("{amount}", "12");
      }
    } else if (
      traveler.passengerType === "STU" ||
      traveler.passengerType === "YTH"
    ) {
      const maxAge = itinerary?.youthStudentMaxAge || MAX_PASSENGER_AGE;

      if (date < ageRestrictions.youthStudentMaxAge) {
        return translations.formValidations.maxAge.replace(
          "{amount}",
          String(maxAge),
        );
      }
      if (date > ageRestrictions.childMaxAge) {
        return translations.formValidations.minAge.replace("{amount}", "12");
      }
    } else if (
      traveler.passengerType === "CNN" ||
      traveler.passengerType === "CHD"
    ) {
      if (date < ageRestrictions.childMaxAge) {
        return translations.formValidations.maxAge.replace("{amount}", "12");
      }
      if (date > ageRestrictions.infantMaxAge) {
        return translations.formValidations.minAge.replace("{amount}", "2");
      }
      return true;
    } else if (traveler.passengerType === "INF") {
      if (date < ageRestrictions.infantMaxAge) {
        return translations.formValidations.maxAge.replace("{amount}", "2");
      }
    }

    if (date < ageRestrictions.passengerMaxAge) {
      return translations.formValidations.maxAge.replace(
        "{amount}",
        String(MAX_PASSENGER_AGE),
      );
    }

    return true;
  };

  const validateCardExpiryDate = (value: string) => {
    const date = new Date(value);
    const travelMonth = format(lastTravelDateTime, "yyyy-MM");
    const inputMonth = format(date, "yyyy-MM");

    if (travelMonth > inputMonth) {
      return translations.formValidations.maxExpiryDate;
    }

    return true;
  };

  const getTranslatedPassengerType = (traveler: ITraveler): string => {
    const objectKey =
      traveler.passengerType as keyof typeof translations.passengerTypesMap;
    return ` - ${translations.passengerTypesMap[objectKey].single}`;
  };

  const onChangeStudentCard = (value: string, index: number = 0) => {
    const paymentLineStudentCard: IPaymentLine = {
      key: `studentCard-${index}`,
      label: content.studentCard.studentCard,
      amount: youthStudentCardPrice ? youthStudentCardPrice : 0,
      index: index,
    };
    if (value === "yes") {
      dispatch(removePaymentLine(paymentLineStudentCard));
    } else if (value === "no") {
      dispatch(addPaymentLine(paymentLineStudentCard));
    }
  };

  useEffect(() => {
    watch((data) => {
      const values = data as ITravelerFormFields;
      dispatch(setTravelerState({ index, values }));
    });
  }, [watch]);

  useEffect(() => {
    onValidityChange(isValid);
  }, [isValid]);

  useEffect(() => {
    if (shouldTriggerValidation) {
      trigger();
    }
  }, [shouldTriggerValidation]);

  useEffect(() => {
    if (sameAsContactPerson) {
      handleCheckboxClick(true);
    }
  }, [contactInformation]);

  const genderRadioGroupItems = [
    {
      value: "female",
      label: translations.contactInformation.female,
    },
    {
      value: "male",
      label: translations.contactInformation.male,
    },
  ];

  const hasStudentCardRadioGroupItems = [
    {
      value: "no",
      label: content.studentCard.studentCardButtonNo,
    },
    {
      value: "yes",
      label: content.studentCard.studentCardButtonYes,
    },
  ];

  return (
    <Box sx={sxStyles.formWrapper} key={index}>
      <Box sx={sxStyles.travelerTitleWrapper}>
        <Box sx={sxStyles.travelerTitle}>
          <FormLabel sx={sxStyles.headerLabel}>
            <Typography variant="titleMedium">
              {content.traveler} {index + 1}
            </Typography>
            <Typography variant="titleMedium" weight="bold">
              {getTranslatedPassengerType(traveler)}
            </Typography>
          </FormLabel>
        </Box>
      </Box>
      {index === 0 && (
        <FormControlLabel
          sx={sxStyles.travelerCheckbox}
          value="start"
          control={<Checkbox checked={sameAsContactPerson} />}
          label={content.sameAsContactPerson}
          labelPlacement="end"
          onChange={(e, value) => handleCheckboxClick(value)}
        />
      )}
      <Box sx={sxStyles.travelerFormWrapper}>
        <Box
          sx={{
            gridColumn: "1 / 3",
            mb: 1,
          }}
        >
          <Collapse in={showNameLengthNotification}>
            <NameLengthNotification
              content={content.nameLengthNotification}
              alertVariant={isDesktop ? "neutral" : "grey"}
            />
          </Collapse>
        </Box>
        <TextField
          control={control}
          errors={errors}
          required
          noSpecialCharacters
          label={formLabels.firstName}
          defaultValue={traveler.formFields.firstName}
          name="firstName"
          disabled={sameAsContactPerson && index === 0}
          formValidationTranslations={translations.formValidations}
          onFocus={() => setNameHasFocus(true)}
          onBlurCapture={() => setNameHasFocus(false)}
        />
        <TextField
          control={control}
          errors={errors}
          noSpecialCharacters
          label={formLabels.middleName}
          defaultValue={traveler.formFields.middleName}
          name="middleName"
          disabled={sameAsContactPerson && index === 0}
          formValidationTranslations={translations.formValidations}
        />
        <TextField
          control={control}
          errors={errors}
          noSpecialCharacters
          required
          label={formLabels.lastName}
          defaultValue={traveler.formFields.lastName}
          name="lastName"
          disabled={sameAsContactPerson && index === 0}
          formValidationTranslations={translations.formValidations}
        />

        <DatePicker
          control={control}
          errors={errors}
          label={formLabels.dateOfBirth}
          defaultValue={traveler.formFields.dateOfBirth}
          minDate={getMinDate()}
          maxDate={getMaxDate()}
          disableFuture
          name="dateOfBirth"
          formValidationTranslations={translations.formValidations}
          sx={{ width: "100%" }}
          rules={{
            validate: {
              required: (value) => {
                if (!isValidFns(value)) {
                  return translations.formValidations.required;
                }
              },
              validateAgeRestrictions,
            },
          }}
        />
      </Box>
      <Box sx={sxStyles.checkboxWrapper}>
        <FormLabel sx={sxStyles.headerLabel}>
          <Typography
            variant="titleMedium"
            weight="bold"
            sx={
              sameAsContactPerson && index === 0
                ? { color: "action.disabled" }
                : {}
            }
          >
            {contactInformationContent.gender}
          </Typography>
        </FormLabel>
        <Typography
          variant="bodySmall"
          sx={
            sameAsContactPerson && index === 0
              ? { color: "action.disabled" }
              : {}
          }
        >
          {contactInformationContent.genderDescription}
        </Typography>
        <RadioGroup
          control={control}
          errors={errors}
          defaultValue={traveler.formFields.genderType}
          disabled={sameAsContactPerson && index === 0}
          required
          row
          name={"genderType--" + index}
          items={genderRadioGroupItems}
          formValidationTranslations={translations.formValidations}
        />
      </Box>

      {traveler.passengerType === "STU" && (
        <>
          <Box sx={sxStyles.checkboxWrapper}>
            <FormLabel sx={sxStyles.headerLabel}>
              <Typography
                variant="titleMedium"
                weight="bold"
                sx={sxStyles.headerWithTooltip}
              >
                {content.studentCard.studentCard}{" "}
                <Tooltip title={content.studentCard.studentCardWhatIs}>
                  <InfoOutlined fontSize="small" />
                </Tooltip>
              </Typography>
            </FormLabel>

            <Typography variant="bodySmall">
              {content.studentCard.studentCardDescription}
            </Typography>
            {traveler.formFields.hasStudentCard?.toString()}
            <RadioGroup
              control={control}
              errors={errors}
              defaultValue={traveler.formFields.hasStudentCard}
              required
              row
              name="hasStudentCard"
              items={hasStudentCardRadioGroupItems}
              formValidationTranslations={translations.formValidations}
              onChange={(e) => onChangeStudentCard(e.target.value, index)}
            />
          </Box>

          <Collapse
            in={!!traveler.formFields.hasStudentCard}
            timeout={theme.transitions.duration.enteringScreen}
          >
            {traveler.formFields.hasStudentCard === "yes" && (
              <Box sx={sxStyles.travelerFormWrapper}>
                <TextField
                  control={control}
                  errors={errors}
                  required
                  label={content.studentCard.studentCardNumberLabel}
                  defaultValue={traveler.formFields.discountCardNumber}
                  name="discountCardNumber"
                  formValidationTranslations={translations.formValidations}
                  placeholder="X 123 456 789 X"
                  rules={{
                    pattern: {
                      value: /^([A-Za-z]\s*){1}(\d{3}\s*){4}([A-Za-z]){1}$/,
                      message: translations.formValidations.isicCardNumber,
                    },
                  }}
                />
                <DatePicker
                  control={control}
                  errors={errors}
                  required
                  views={["year", "month"]}
                  format="MM/yyyy"
                  label={content.studentCard.cardExpiryDateLabel}
                  defaultValue={traveler.formFields.cardExpiryDate}
                  rules={{
                    validate: { validateCardExpiryDate },
                  }}
                  name="cardExpiryDate"
                  formValidationTranslations={translations.formValidations}
                />
              </Box>
            )}
            {traveler.formFields.hasStudentCard === "no" && (
              <Box sx={sxStyles.travelerFormWrapper}>
                <TextField
                  control={control}
                  errors={errors}
                  required
                  label={content.studentCard.studentCardEmail}
                  defaultValue={traveler.formFields.email}
                  name="email"
                  email
                  formValidationTranslations={translations.formValidations}
                />
                <TextField
                  control={control}
                  errors={errors}
                  required
                  label={content.studentCard.studentCardNameOfSchool}
                  name="nameOfSchool"
                  defaultValue={traveler.formFields.nameOfSchool}
                  formValidationTranslations={translations.formValidations}
                />
              </Box>
            )}
          </Collapse>
        </>
      )}
    </Box>
  );
}

export const sxStyles: { [key: string]: SxProps } = {
  formWrapper: {
    borderBottom: "1px solid ",
    borderBottomColor: "grey.300",
    mb: 4,
    mt: { md: 4 },
    p: { xs: 2, md: 4 },
    pt: { md: 3 },
    backgroundColor: { md: "grey.100" },
  },
  headerLabel: {
    display: "flex",
    alignItems: "center",
  },
  headerWithTooltip: {
    display: "flex",
    alignItems: "center",
  },
  checkboxWrapper: {
    display: "flex",
    flexDirection: "column",
    my: 4,
    mx: 0,
  },
  travelerTitleWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    pb: 1.5,
    py: { xs: 2, md: 0 },
  },
  travelerTitle: {
    display: "flex",
    whiteSpace: "pre",
    textTransform: "capitalize",
  },
  travelerCheckbox: {
    display: "inline-flex",
  },
  travelerFormWrapper: {
    display: { xs: "flex", sm: "grid" },
    gridTemplateColumns: { md: "repeat(2, 1fr)" },
    flexDirection: "column",
    gap: 2,
    mt: 1,
    p: { md: 0 },
    border: { md: "none" },
  },
};
